// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentCitationLinks.test.tsx
// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.review-viewer.spec.ts
import { Warning } from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AgentSourceCitationLink } from "./citation-links/AgentSourceCitationLink";
import { ArtworkRegionCitationLink } from "./citation-links/ArtworkRegionCitationLink";
import { GraphCitationLink } from "./citation-links/GraphCitationLink";
import { KnowledgeDbCitationLink } from "./citation-links/KnowledgeDbCitationLink";
import { KnowledgeRuleCitationLink } from "./citation-links/KnowledgeRuleCitationLink";
import { NavigableFileCitationLink } from "./citation-links/NavigableFileCitationLink";
import { TextCitationLink } from "./citation-links/TextCitationLink";

import {
  type LinkableMemory,
  collectMemoryCitations,
  numberMemoryCitations,
  supportedMemories,
} from "../../../utils/memoryCitationFields";
import {
  type CitationTarget,
  type TicketMessageCitation,
  isAgentSourceCitation,
  isArtworkRegionCitation,
  isGraphCitation,
  isKnowledgeDbCitation,
  isKnowledgeRuleCitation,
  isTextCitation,
  resolveCitationVariant,
} from "../utils/commentSourceUtils";

/** Renders the citation chips under an Ask Puntt review comment, one per citation variant.
 *
 * This file is the dispatcher only: every chip lives in `./citation-links/`, so
 * adding a variant means a new module plus one branch here rather than another
 * hundred lines in a file that already sat at the 400-line ceiling. */
export function CommentCitationLinks({
  citations,
  onCitationClick,
  vertical = false,
  memoryScope = "comment-citations",
  description,
}: {
  citations: TicketMessageCitation[];
  onCitationClick?: (citation: CitationTarget) => void;
  /** Stack each citation on its own line instead of wrapping inline. */
  vertical?: boolean;
  /** The comment's id, so two comments citing one memory keep distinct chip testids. */
  memoryScope?: string;
  /** The comment's text, so the M numbers follow the sentences' order there, as the body's tint does. */
  description?: string;
}) {
  const { t } = useTranslation("ui");
  // The saved memories each graph citation carries that name their sentence,
  // numbered M1, M2 ... over the whole list so a chip keeps its number whichever
  // citation it rides on. A memory named by two citations is drawn once.
  const memoriesByCitation = useMemo(() => {
    const numbers = new Map(
      numberMemoryCitations(description, collectMemoryCitations(citations)).map(
        (memory) => [memory.id, memory.number] as const,
      ),
    );
    const placed = new Set<string>();
    return citations.map((citation): LinkableMemory[] => {
      if (!isGraphCitation(citation)) return [];
      const fresh = supportedMemories(
        collectMemoryCitations([citation]),
      ).filter((memory) => !placed.has(memory.id));
      fresh.forEach((memory) => placed.add(memory.id));
      return fresh.map((memory) => ({
        ...memory,
        number: numbers.get(memory.id) ?? 0,
        scope: memoryScope,
      }));
    });
  }, [citations, memoryScope, description]);
  if (citations.length === 0) return null;

  return (
    <Flex
      gap="2"
      className="mt-2 shrink-0"
      {...(vertical
        ? { direction: "column" as const, align: "start" as const }
        : { wrap: "wrap" as const })}
    >
      {citations.map((citation, idx) => {
        // ONE dispatcher, shared with the tickets comment drawer
        // (`comment-drawer/Comment.tsx`). The two surfaces used to walk their
        // own if-chains over this union and disagreed about which citations
        // were renderable; `resolveCitationVariant` owns the order (specific
        // discriminants first, generic `text` last) so a new variant is
        // handled, or declared unrenderable, in one place.
        switch (resolveCitationVariant(citation)) {
          case "file":
            return (
              <NavigableFileCitationLink
                key={`citation-${idx}`}
                citation={citation}
                index={idx}
                onCitationClick={onCitationClick}
              />
            );
          case "rule":
            return (
              <KnowledgeRuleCitationLink
                key={`citation-${idx}`}
                citation={citation}
              />
            );
          case "graph":
            return (
              <GraphCitationLink
                key={`citation-${idx}`}
                citation={citation}
                memories={memoriesByCitation[idx]}
              />
            );
          case "knowledge-record":
            return (
              <KnowledgeDbCitationLink
                key={`citation-${idx}`}
                citation={citation}
              />
            );
          case "artwork-region":
            return isArtworkRegionCitation(citation) ? (
              <ArtworkRegionCitationLink
                key={`citation-${idx}`}
                citation={citation}
                chipTestId={`comment-panel-citation-artwork-${idx}`}
              />
            ) : null;
          case "agent-source":
            return (
              <AgentSourceCitationLink
                key={`citation-${idx}`}
                citation={citation}
              />
            );
          case "text":
            return (
              <TextCitationLink key={`citation-${idx}`} citation={citation} />
            );
          // A citation that names no file, rule, graph node, KDB record, agent
          // source or excerpt. Nothing to open, so the chip says so instead of
          // linking nowhere.
          case "unrenderable":
          default:
            return (
              <Flex
                key={`citation-${idx}`}
                gap="1"
                align="center"
                className="text-puntt-neutral-gray-9"
              >
                <Text size="1">{t("commentCitationLinks.source")}</Text>
                <Warning size={12} aria-hidden="true" />
              </Flex>
            );
        }
      })}
    </Flex>
  );
}
